import React, { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { previewActivity } from '../../features/preview/previewActivity';
import { webPreviewPlayer } from '../../features/preview/previewPlayer';

function YouTubeFrame({ media }) {
  const { t } = useWebLocale();
  const [status, setStatus] = useState('loading'),
    [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (status !== 'loading') return undefined;
    const timer = setTimeout(() => setStatus('failed'), 15000);
    return () => clearTimeout(timer);
  }, [status, attempt]);
  useLayoutEffect(() => {
    // A blocked cross-origin iframe can still emit load: show recovery instead of a blank player.
    const blocked = (event) => {
      if (
        event.disposition === 'report' ||
        !['frame-src', 'child-src', 'default-src'].includes(event.effectiveDirective)
      )
        return;
      try {
        if (new URL(event.blockedURI).origin === new URL(media.embed).origin) setStatus('failed');
      } catch {
        /* Unrelated policy report. */
      }
    };
    document.addEventListener('securitypolicyviolation', blocked);
    return () => document.removeEventListener('securitypolicyviolation', blocked);
  }, [media.embed]);
  if (status === 'failed')
    return (
      <Box
        role="status"
        sx={{
          p: 2,
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Typography
          sx={{ color: 'var(--settings-muted)', fontSize: '.875rem', lineHeight: '1.3125rem' }}
        >
          {t('mediaError')}
        </Typography>
        <Button
          sx={{
            color: 'var(--settings-text)',
            fontSize: '.8125rem',
            lineHeight: '1.125rem',
            textTransform: 'none',
            minHeight: 44,
            justifyContent: 'flex-start',
            p: 0,
          }}
          onClick={() => {
            setAttempt((v) => v + 1);
            setStatus('loading');
          }}
        >
          {t('retry')}
        </Button>
        <Link
          href={media.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'var(--settings-text)',
            display: 'flex',
            alignItems: 'center',
            minHeight: 44,
            fontSize: '.8125rem',
            lineHeight: '1.125rem',
          }}
        >
          {t('original')}
        </Link>
      </Box>
    );
  return (
    <Box sx={{ position: 'relative' }}>
      {status === 'loading' && (
        <Typography
          role="status"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            pointerEvents: 'none',
            color: 'var(--settings-muted)',
            fontSize: '.8125rem',
            lineHeight: '1.125rem',
          }}
        >
          YouTube · {t('mediaLoading')}
        </Typography>
      )}
      <Box
        component="iframe"
        key={attempt}
        title="YouTube media"
        src={media.embed}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        allow="encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        onLoad={() => setStatus((previous) => (previous === 'failed' ? previous : 'ready'))}
        onError={() => setStatus('failed')}
        sx={{
          width: '100%',
          height: 'auto',
          minHeight: 200,
          aspectRatio: '16 / 9',
          border: 0,
          display: 'block',
        }}
      />
    </Box>
  );
}
export default function AutomaticFeedMedia({ media, id, viewport }) {
  const node = useRef(null),
    { t, locale } = useWebLocale();
  useLayoutEffect(() => viewport.register(id, node.current), [id, viewport]);
  const active = useSyncExternalStore(
    (listener) => viewport.subscribe(id, listener),
    () => viewport.active(id),
    () => false,
  );
  const previewPlaying = useSyncExternalStore(
    previewActivity.subscribe,
    previewActivity.getSnapshot,
    () => false,
  );
  return (
    <Box
      ref={node}
      data-feed-media-id={id}
      sx={{
        my: '12px',
        width: '100%',
        minWidth: 0,
        minHeight: 200,
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: 'var(--settings-surface, #eee)',
      }}
    >
      {active ? (
        <YouTubeFrame media={media} />
      ) : (
        <Box sx={{ width: '100%', minHeight: 200, aspectRatio: '16 / 9' }}>
          {previewPlaying ? (
            <Button
              onClick={() => webPreviewPlayer.pause()}
              sx={{
                minHeight: 44,
                m: 2,
                color: 'var(--settings-text)',
                fontSize: '.8125rem',
                lineHeight: '1.125rem',
                textTransform: 'none',
              }}
            >
              {locale === 'en'
                ? 'Watch YouTube (pause preview)'
                : 'YouTube 보기 · 미리듣기 일시정지'}
            </Button>
          ) : (
            <Typography
              sx={{
                p: 2,
                color: 'var(--settings-muted)',
                fontSize: '.8125rem',
                lineHeight: '1.125rem',
              }}
            >
              YouTube · {t('mediaLoading')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
